class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Mouse drag-to-scroll for the cart upsell carousel. Delegated because the drawer re-renders its contents.
(() => {
  let drag = null;

  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    const list = event.target.closest('.cart-upsell__list');
    if (!list || event.target.closest('button')) return;
    drag = { list, startX: event.clientX, startScroll: list.scrollLeft, moved: false };
  });

  document.addEventListener('pointermove', (event) => {
    if (!drag) return;
    const delta = event.clientX - drag.startX;
    if (!drag.moved && Math.abs(delta) < 5) return;
    if (!drag.moved) {
      drag.moved = true;
      drag.list.classList.add('is-dragging');
    }
    drag.list.scrollLeft = drag.startScroll - delta;
  });

  document.addEventListener('pointerup', () => {
    if (!drag) return;
    const { list, moved } = drag;
    drag = null;
    if (!moved) return;
    list.classList.remove('is-dragging');
    // Swallow the click that follows a drag so links don't open.
    const swallow = (event) => {
      event.preventDefault();
      event.stopPropagation();
    };
    list.addEventListener('click', swallow, { capture: true, once: true });
    setTimeout(() => list.removeEventListener('click', swallow, { capture: true }));
  });
})();

// Free UK delivery progress bar. The drawer re-renders on every cart change,
// so animate from the last known value to the new one.
if (!customElements.get('free-shipping-progress')) {
  let lastProgress = 0;

  customElements.define(
    'free-shipping-progress',
    class FreeShippingProgress extends HTMLElement {
      connectedCallback() {
        this.target = Math.min(parseFloat(this.dataset.progress) || 0, 100);
        this.drawer = this.closest('cart-drawer');
        this.animateFrom(this.drawer?.classList.contains('active') ? lastProgress : 0);
        lastProgress = this.target;

        // Replay when the drawer is opened, since a closed drawer's animation isn't seen.
        if (this.drawer) {
          this.observer = new MutationObserver(() => {
            const isOpen = this.drawer.classList.contains('active');
            if (isOpen && !this.wasOpen) this.animateFrom(0);
            this.wasOpen = isOpen;
          });
          this.wasOpen = this.drawer.classList.contains('active');
          this.observer.observe(this.drawer, { attributes: true, attributeFilter: ['class'] });
        }
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      animateFrom(start) {
        const fill = this.querySelector('.cart-free-shipping-bar__fill');
        fill.style.transition = 'none';
        fill.style.width = `${start}%`;
        fill.getBoundingClientRect(); // commit start width before transitioning
        fill.style.transition = '';
        requestAnimationFrame(() => {
          fill.style.width = `${this.target}%`;
        });
      }
    }
  );
}
